import React from "react";
import { Skeleton, Tooltip } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import "./KpiCard.scss";

/**
 * Premium KPI tile.
 *
 * variant "metric" -> headline number + optional trend pill + optional footer row
 * variant "split"  -> headline number + stacked share bar for two segments
 */
const KpiCard = ({
  title,
  value,
  icon = null,
  accent = "blue",
  trend = null,
  footerLabel,
  footerValue,
  variant = "metric",
  segments = [],
  loading = false,
  onClick,
  hint,
}) => {
  const trendValue = parseFloat(trend);
  const hasTrend =
    trend !== null && trend !== undefined && trend !== "" && !isNaN(trendValue);
  const isUp = trendValue >= 0;

  const total = segments.reduce((sum, s) => sum + (Number(s.value) || 0), 0);

  if (loading) {
    return (
      <div className={`dsh-kpi dsh-kpi--${accent} is-loading`}>
        <Skeleton active paragraph={{ rows: 2, width: ["60%", "80%"] }} title={false} />
      </div>
    );
  }

  const card = (
    <div
      className={`dsh-kpi dsh-kpi--${accent} ${onClick ? "is-clickable" : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === "Enter" || e.key === " ")) onClick(e);
      }}
    >
      <span className="dsh-kpi__glow" aria-hidden="true" />

      <div className="dsh-kpi__top">
        <div className="dsh-kpi__label">
          {icon && <span className="dsh-kpi__icon">{icon}</span>}
          <span className="dsh-kpi__title">{title}</span>
        </div>
        {hasTrend && (
          <span className={`dsh-kpi__trend ${isUp ? "is-up" : "is-down"}`}>
            {isUp ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            {String(trend).replace("-", "")}
          </span>
        )}
      </div>

      <div className="dsh-kpi__value">{value ?? 0}</div>

      {variant === "split" ? (
        <div className="dsh-kpi__split">
          <div className="dsh-kpi__bar">
            {segments.map((s, i) => (
              <Tooltip key={s.name} title={`${s.name}: ${s.value ?? 0}%`}>
                <span
                  className={`dsh-kpi__bar-seg seg-${i}`}
                  style={{
                    width: `${total ? ((Number(s.value) || 0) / total) * 100 : 50}%`,
                  }}
                />
              </Tooltip>
            ))}
          </div>
          <div className="dsh-kpi__legend">
            {segments.map((s, i) => (
              <span className="dsh-kpi__legend-item" key={s.name}>
                <i className={`dot seg-${i}`} />
                {s.name}
                <b>{s.value ?? 0}%</b>
              </span>
            ))}
          </div>
        </div>
      ) : (
        (footerLabel || footerValue) && (
          <div className="dsh-kpi__footer">
            <span>{footerLabel}</span>
            <b>{footerValue}</b>
          </div>
        )
      )}
    </div>
  );

  return hint ? <Tooltip title={hint}>{card}</Tooltip> : card;
};

export default KpiCard;
